import { z } from 'zod';

// ============================================================================
// CONSTANTS
// ============================================================================
const DOCUMENT_CONSTANTS = {
  FILENAME_MAX_LENGTH: 255,
  NAME_MAX_LENGTH: 100,
  MIME_TYPE_MAX_LENGTH: 100,
} as const;

// ============================================================================
// LENIENT SCHEMA (for deserialization)
// ============================================================================
const lenientDocumentNodeSchema = z.object({
  id: z.string(),
  filename: z.string().max(DOCUMENT_CONSTANTS.FILENAME_MAX_LENGTH),
  name: z.string().max(DOCUMENT_CONSTANTS.NAME_MAX_LENGTH),
  mimeType: z.string().max(DOCUMENT_CONSTANTS.MIME_TYPE_MAX_LENGTH),
  for: z.array(z.string()),
}).transform(val => ({
  id: val.id,
  filename: val.filename,
  name: val.name,
  mimeType: val.mimeType,
  for: val.for,
}));

// ============================================================================
// STRICT SCHEMA (for validation)
// ============================================================================
const strictDocumentNodeSchema = z.object({
  id: z.string().min(1, 'ID is required'),
  filename: z.string()
    .min(1, 'Filename is required')
    .max(DOCUMENT_CONSTANTS.FILENAME_MAX_LENGTH, `Filename must be ${DOCUMENT_CONSTANTS.FILENAME_MAX_LENGTH} characters or less`),
  name: z.string()
    .min(1, 'Name is required')
    .max(DOCUMENT_CONSTANTS.NAME_MAX_LENGTH, `Name must be ${DOCUMENT_CONSTANTS.NAME_MAX_LENGTH} characters or less`),
  mimeType: z.string()
    .min(1, 'MIME type is required')
    .max(DOCUMENT_CONSTANTS.MIME_TYPE_MAX_LENGTH, `MIME type must be ${DOCUMENT_CONSTANTS.MIME_TYPE_MAX_LENGTH} characters or less`)
    .regex(/^[a-z]+\/[a-z0-9\-\+\.]+$/i, 'Must be a valid MIME type (e.g., application/pdf, image/png)'),
  for: z.array(z.string()).min(0, 'For array is required (can be empty)'),
}).transform(val => ({
  id: val.id,
  filename: val.filename,
  name: val.name,
  mimeType: val.mimeType,
  for: val.for,
}));

// ============================================================================
// TYPE
// ============================================================================
export type DocumentNode = z.infer<typeof lenientDocumentNodeSchema>;

// ============================================================================
// NAMESPACE (for Constants and Methods)
// ============================================================================
export namespace DocumentNode {
  export const Constants = DOCUMENT_CONSTANTS;

  export function deserialize(data: unknown): DocumentNode {
    return lenientDocumentNodeSchema.parse(data);
  }

  export function create(data: unknown): DocumentNode {
    return strictDocumentNodeSchema.parse(data);
  }

  /**
   * Get the file extension from the filename
   */
  export function getFileExtension(node: DocumentNode): string {
    const parts = node.filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  }

  /**
   * Check if the document is associated with specific entities
   */
  export function isForEntities(node: DocumentNode): boolean {
    return node.for.length > 0;
  }

  /**
   * Get the display name (use name if available, otherwise filename)
   */
  export function getDisplayName(node: DocumentNode): string {
    return node.name || node.filename;
  }
}
